/**
 * Guide sound cues, synthesised with the Web Audio API (no assets) on one shared context.
 *
 * The owner's sound rules (2026-10-16):
 * - OFF by default. The ledger toggle (localStorage `foxet-guide-sound`, 'on' to enable) turns cues
 *   on, and only after a real user gesture (click, key, wheel, pointer, touch; not `scroll`). The first
 *   arrival on a fresh load is therefore silent.
 * - Every cue ≤ 220ms except the arc whoosh (300ms); all through one master gain at −20dB; a 6ms attack
 *   and a 40–120ms exponential release on every oscillator; the land blip gets a 2ms lowpass sweep.
 * - One key, A major: 330 (E4) · 440 (A4) · 554 (C#5) · 659 (E5) · 880 (A5). The hop chirp rises
 *   A4→C#5 and the land blip answers on E5, a fifth above, so they read as one gesture.
 * - Mr. Clippy's cues are the bright ones (chirps, taps, the arpeggio); Foxy's is a soft "mm".
 * - One cue at a time; at most one per 350ms, except the landing sequence (whoosh, land, taps) and the
 *   email's lighter whoosh, which are scripted sequences and exempt.
 * - A cue plays only when its motion is in the viewport: pass the element.
 *
 * Each scheduled cue dispatches `fx-guide-cue` on `document` with the cue name as `detail`.
 */
const KEY = 'foxet-guide-sound';
const MIN_GAP_MS = 350;
const MASTER_DB = -20;
let interacted = false;
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let lastAt = 0;
let tail: GainNode | null = null;

if (typeof window !== 'undefined') {
  const mark = () => {
    interacted = true;
  };
  for (const ev of ['click', 'keydown', 'wheel', 'pointerdown', 'touchstart']) window.addEventListener(ev, mark, { passive: true });
}

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on';
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? 'on' : 'off');
  } catch {
    /* storage unavailable: the toggle still works for this page */
  }
}

const dB = (v: number) => Math.pow(10, v / 20);

function context(): AudioContext | null {
  if (!interacted || !soundEnabled()) return null;
  try {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctx) {
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = dB(MASTER_DB);
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

type Builder = (c: AudioContext, out: AudioNode, t0: number) => void;
export type CueOpts = {
  /** part of a scripted sequence: skip the 350ms cap */
  exempt?: boolean;
  /** the moving element: the cue plays only while it is in the viewport */
  el?: Element | null;
};

function visible(el: Element | null | undefined): boolean {
  if (!el) return true;
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
}

/** One cue at a time: cancel the previous tail, respect the cap and the visibility rule, then build. */
function schedule(name: string, level: number, build: Builder, opts: CueOpts = {}) {
  const now = performance.now();
  if (!opts.exempt && now - lastAt < MIN_GAP_MS) return false;
  if (!visible(opts.el)) return false;
  const c = context();
  if (!c || !master) return false;
  lastAt = now;
  const t0 = c.currentTime;
  if (tail) {
    try {
      tail.gain.cancelScheduledValues(t0);
      tail.gain.setTargetAtTime(0, t0, 0.008);
    } catch {
      /* already gone */
    }
  }
  const out = c.createGain();
  out.gain.value = dB(level);
  out.connect(master);
  tail = out;
  build(c, out, t0);
  document.dispatchEvent(new CustomEvent('fx-guide-cue', { detail: name }));
  return true;
}

/** An oscillator with a 6ms attack and an exponential release (40–120ms). */
function tone(c: AudioContext, out: AudioNode, t0: number, type: OscillatorType, from: number, to: number | null, start: number, dur: number, release = 0.06) {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0 + start);
  g.gain.exponentialRampToValueAtTime(1, t0 + start + 0.006);
  g.gain.setValueAtTime(1, t0 + start + dur);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur + release);
  g.connect(out);
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(from, t0 + start);
  if (to) o.frequency.exponentialRampToValueAtTime(to, t0 + start + dur);
  o.connect(g);
  o.start(t0 + start);
  o.stop(t0 + start + dur + release + 0.01);
  return o;
}

function noise(c: AudioContext, out: AudioNode, t0: number, start: number, dur: number, gain: number, filterHz?: number) {
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0 + start);
  g.gain.exponentialRampToValueAtTime(gain, t0 + start + 0.006);
  if (filterHz) {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = filterHz;
    src.connect(f);
    f.connect(g);
  } else {
    src.connect(g);
  }
  g.connect(out);
  src.start(t0 + start);
}

/** A bandpass-filtered noise sweep: the whoosh. */
function sweep(c: AudioContext, out: AudioNode, t0: number, ms: number, f0: number, f1: number, q: number) {
  const len = Math.floor(c.sampleRate * (ms / 1000));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) d[i] = (Math.random() * 2 - 1) * Math.sin((i / len) * Math.PI);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = q;
  f.frequency.setValueAtTime(f0, t0);
  f.frequency.exponentialRampToValueAtTime(f1, t0 + ms / 1000);
  src.connect(f);
  f.connect(out);
  src.start(t0);
}

// ── Mr. Clippy: the bright cues ──
/** hop start: a chirp rising A4→C#5, 110ms. */
export const hop = (o?: CueOpts) => schedule('hop', -2, (c, out, t0) => { tone(c, out, t0, 'sine', 440, 554, 0, 0.11, 0.05); }, o);
/** hop landing: a soft 30ms filtered tap. */
export const hopLand = (o?: CueOpts) => schedule('hopLand', -4, (c, out, t0) => { noise(c, out, t0, 0, 0.03, 1, 1200); }, o);
/** the land blip: E5 (a fifth above the chirp's A) through a lowpass that opens over 2ms, 90ms. */
export const land = (o?: CueOpts) => schedule('land', 0, (c, out, t0) => {
  const f = c.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.setValueAtTime(300, t0);
  f.frequency.exponentialRampToValueAtTime(6000, t0 + 0.002);
  f.connect(out);
  tone(c, f, t0, 'sine', 659, null, 0, 0.09, 0.08);
  noise(c, out, t0, 0, 0.03, 0.4);
}, o);
/** bounce taps 2 and 3: 25ms filtered noise. */
export const tap = (o?: CueOpts) => schedule('tap', -4, (c, out, t0) => { noise(c, out, t0, 0, 0.025, 1, 1500); }, o);
/** wave: E5 then A5, 60ms each, 40ms apart. */
export const wave = (o?: CueOpts) => schedule('wave', -2, (c, out, t0) => { tone(c, out, t0, 'triangle', 659, null, 0, 0.06); tone(c, out, t0, 'triangle', 880, null, 0.1, 0.06); }, o);
/** curious: C#5 rising to E5, 100ms. */
export const curious = (o?: CueOpts) => schedule('curious', -4, (c, out, t0) => { tone(c, out, t0, 'triangle', 554, 659, 0, 0.1); }, o);
/** nod: two A4 taps, 50ms each. */
export const nod = (o?: CueOpts) => schedule('nod', -4, (c, out, t0) => { tone(c, out, t0, 'sine', 440, null, 0, 0.05, 0.04); tone(c, out, t0, 'sine', 440, null, 0.1, 0.05, 0.04); }, o);
/** cheer: the A-major arpeggio A4 C#5 E5 A5, 45ms each (180ms). */
export const cheer = (o?: CueOpts) => schedule('cheer', 0, (c, out, t0) => { [440, 554, 659, 880].forEach((f, i) => tone(c, out, t0, 'triangle', f, null, i * 0.045, 0.045, 0.06)); }, o);
/** point: an A5 tick, 40ms. */
export const point = (o?: CueOpts) => schedule('point', -6, (c, out, t0) => { tone(c, out, t0, 'sine', 880, null, 0, 0.04, 0.04); }, o);
/** think: a low E4, 120ms. */
export const think = (o?: CueOpts) => schedule('think', -6, (c, out, t0) => { tone(c, out, t0, 'sine', 330, null, 0, 0.12, 0.08); }, o);
/** speak (buyer lines): a short C#5, 80ms. */
export const speak = (o?: CueOpts) => schedule('speak', -6, (c, out, t0) => { tone(c, out, t0, 'sine', 554, null, 0, 0.08, 0.06); }, o);
/** bubble: an A5 pop, 30ms. */
export const bubble = (o?: CueOpts) => schedule('bubble', -8, (c, out, t0) => { tone(c, out, t0, 'sine', 880, 1100, 0, 0.03, 0.04); }, o);
/** whoosh: a filtered-noise sweep, 300ms (the arrival and the exit). */
export const whoosh = (o?: CueOpts) => schedule('whoosh', -4, (c, out, t0) => sweep(c, out, t0, 300, 400, 2400, 1.2), o);
/** a lighter whoosh for the follow-up email, 220ms. */
export const whooshLight = (o?: CueOpts) => schedule('whooshLight', -8, (c, out, t0) => sweep(c, out, t0, 220, 900, 3200, 1.5), o);
/** the toss tick: 20ms at −30dB (−10 under the master). */
export const tick = (o?: CueOpts) => schedule('tick', -10, (c, out, t0) => { tone(c, out, t0, 'sine', 880, null, 0, 0.02, 0.04); }, o);
// ── Foxy: the soft hum ──
/** mm: E4 sine, 90ms, at the start of each Foxet line. */
export const mm = (o?: CueOpts) => schedule('mm', -4, (c, out, t0) => { tone(c, out, t0, 'sine', 330, null, 0, 0.09, 0.1); }, o);
/** listen is silent by design. */
export const listen = () => false;

export const cues = { hop, hopLand, land, tap, wave, curious, nod, cheer, point, think, speak, bubble, whoosh, whooshLight, tick, mm, listen } as const;
export type CueName = keyof typeof cues;
